<script setup lang="ts">
import { Mailing } from '@/@types'
import { Item } from '@/@types/cart'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatText } from '@/helpers/format-text'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { ArrowLeftCircle, Loader2, Ticket } from 'lucide-vue-next'

interface MailingProps extends Mailing {
    item: Omit<Item, 'mailings'>
}

defineProps<{
    mailing: MailingProps
}>()

const form = useForm({
    full_name: '',
    email: '',
})
</script>

<template>
    <Head :title="$t('Link participant')" />
    <LightLayout>
        <main class="mx-auto max-w-3xl p-4">
            <section class="flex items-center gap-2">
                <Link :href="`/admin/orders/${mailing.item.order_id}`" aria-label="Back to order">
                    <ArrowLeftCircle class="h-8 w-8 text-slate-800" stroke-width="1.2" />
                </Link>

                <h1 class="text-2xl font-semibold text-gray-800">
                    {{ $t('Link participant') }}
                </h1>
            </section>

            <div
                class="mt-8 flex w-full flex-wrap items-center justify-between gap-4 rounded-[10px] px-4 py-6 shadow-[0_4px_12px_0px_rgba(12,12,13,.08)]"
            >
                <div class="flex items-center gap-2 font-semibold">
                    <div class="flex size-11 items-center justify-center rounded-full bg-cyan-50 text-cyan-700">
                        <Ticket />
                    </div>
                    {{ $t(mailing.item.ticket.category) }}
                </div>
            </div>

            <section class="mt-12">
                <form
                    @submit.prevent="
                        form
                            .transform((data) => ({
                                mailing: {
                                    email: data.email,
                                    personal_info_attributes: {
                                        full_name: data.full_name,
                                    },
                                },
                            }))
                            .post(`/admin/items/${mailing.item_id}/mailings`, {
                                replace: true,
                                preserveState: true,
                                onSuccess: () => {
                                    form.reset()
                                    router.flushAll()
                                },
                            })
                    "
                >
                    <fieldset>
                        <legend class="font-sans text-lg font-semibold text-gray-800">
                            {{ $t('Personal data') }}
                        </legend>
                        <p class="text-sm">
                            {{ $t('When requesting completion, we will send an email so that the participant can finish filling in the data.') }}
                        </p>

                        <div class="mt-8 grid gap-4 md:grid-cols-2 lg:gap-8">
                            <div class="flex flex-col gap-1.5">
                                <Label required for="full_name">{{ $t('Full name') }}</Label>
                                <Input
                                    type="text"
                                    v-model="form.full_name"
                                    :placeholder="$t('Full name')"
                                    @input="(e: Event) => (form.full_name = formatText((e.target as HTMLInputElement).value))"
                                />
                                <p v-if="form.errors.full_name" class="text-destructive text-xs">{{ $t(form.errors.full_name) }}</p>
                            </div>
                            <div class="flex flex-col gap-1.5">
                                <Label required for="email">{{ $t('E-mail') }}</Label>
                                <Input type="text" v-model="form.email" placeholder="email@email.com" />
                                <p v-if="form.errors.email" class="text-destructive text-xs">{{ form.errors.email }}</p>
                            </div>
                            <Button class="mt-6" :disabled="form.processing">
                                <template v-if="!form.processing">
                                    {{ $t('Send') }}
                                </template>
                                <template v-if="form.processing">
                                    <span>
                                        <Loader2 class="animate-spin" />
                                        <span class="sr-only">{{ $t('Sending') }}</span>
                                    </span>
                                </template>
                            </Button>
                        </div>
                    </fieldset>
                </form>
            </section>
        </main>
    </LightLayout>
</template>
